import { UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ImpersonationBroker, asApiError } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { KEYWORD_PINS_TYPE, type KeywordPinView } from '../pins/serverTypes';
import { queryKeys } from './queryKeys';

export type UpdateKeywordPinVariables = {
	id: string;
	row: KeywordPinView;
};

/**
 * Update a keyword pin via `PUT user/coffee/customizations/keywordPins/{id}`
 * (Real-Magic/Levitate-API#11465). The full row is sent; the server preserves
 * its own fields (id/creatorId/accountId/creationDate). Used for editing a
 * custom pin and for editing/disabling a built-in that already has an override
 * row. On success the `keywordPins` query is invalidated.
 */
export function useUpdateKeywordPin(): UseMutationResult<KeywordPinView, Error, UpdateKeywordPinVariables> {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation<KeywordPinView, Error, UpdateKeywordPinVariables>({
		mutationFn: async ({ id, row }) => {
			const result = await userSession.webServiceHelper.callWebServiceAsync<KeywordPinView>(
				ImpersonationBroker.composeApiUrl({ urlPath: `user/coffee/customizations/${KEYWORD_PINS_TYPE}/${id}` }),
				'PUT',
				row
			);
			if (!result.success) {
				throw asApiError(result);
			}
			return result.value;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.focusedView.keywordPins() });
		},
		onError: () => {
			toast.error('Failed to save keyword pin');
		},
	});
}
